/* eslint-disable no-plusplus */
/* eslint-disable no-param-reassign */
import {
  Currencies,
  ExtendedCurrencies,
  CurrencyLocales,
} from '@constants/enums';
import { DateTime } from 'luxon';

/* eslint-disable prefer-destructuring */
export const snakeToCamel = (snakeValue: string): string =>
  snakeValue
    .toLowerCase()
    .replace(/([-_][a-z])/g, (group) =>
      group.toUpperCase().replace('-', '').replace('_', '')
    );

export const camelCaseMapData = <D, M>(data: {
  [Property in keyof D]: D[Property];
}): M => {
  const mappedData = Object.entries(data).map(([key, value]) => [
    snakeToCamel(key),
    value,
  ]);

  return Object.fromEntries(mappedData);
};

export const formatCurrency = (
  amount: number | string = 0,
  decimalDigits = 2,
  mindecimalDigits = 2,
  currency = 'MXN'
): string => {
  if (typeof amount === 'string') {
    amount = amount.replace(/[^0-9.-]/g, '');
  }
  const value = Number(amount);
  if (Number.isNaN(value)) return '';
  const locale =
    CurrencyLocales[currency as keyof typeof CurrencyLocales] || 'es-MX';
  return new Intl.NumberFormat(locale, {
    currency,
    style: 'decimal',
    minimumFractionDigits: mindecimalDigits,
    maximumFractionDigits: decimalDigits,
  }).format(value);
};

export const deepSnakeToCamel = (data: object) => {
  const newO: typeof data = {};
  let origKey;
  let newKey: keyof typeof newO;
  let value;
  if (data instanceof Array) {
    return data.map((key) => {
      if (typeof key === 'object') {
        key = deepSnakeToCamel(key);
      }
      return key;
    });
  }
  // eslint-disable-next-line no-restricted-syntax
  for (origKey in data) {
    // eslint-disable-next-line no-prototype-builtins
    if (data.hasOwnProperty(origKey)) {
      newKey = snakeToCamel(origKey) as keyof typeof newO;
      // any needed to validate instanceof and possible constructor
      value = data[origKey as keyof typeof data] as any;
      if (
        value instanceof Array ||
        (value !== null && value.constructor === Object)
      ) {
        value = deepSnakeToCamel(value);
      }
      // any needed to allow key insertion
      (newO as any)[newKey] = value;
    }
  }

  return newO;
};

export const formatCurrencyToNumber = (currency: string): number => {
  try {
    return Number(currency.replace(/[^0-9.-]+/g, ''));
  } catch (error) {
    return Number(currency);
  }
};

export const convertDateToHumanReadable = (
  date: string,
  lang: string,
  dateFormat = DateTime.DATE_MED
) =>
  DateTime.fromISO(date).toLocaleString(dateFormat, {
    locale: lang,
  });

export const convertDateToHumanReadableFromJsDate = (
  date: string,
  lang: string,
  dateFormat = DateTime.DATE_MED
) =>
  DateTime.fromJSDate(new Date(date)).toLocaleString(dateFormat, {
    locale: lang,
  });

export const humanFileSize = (bytes: number, dp = 1) => {
  if (Math.abs(bytes) < 1024) {
    return `${bytes} B`;
  }

  const units = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= 1024;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= 1024 && u < units.length - 1);

  return `${bytes.toFixed(dp)} ${units[u]}`;
};

const LAST_DIGITS_TO_SHOW = 4;

export const protectString = (data: string, show = LAST_DIGITS_TO_SHOW) => {
  if (data && data.length) {
    const prevLength = data.length - show;
    const trimmed = data.slice(prevLength, data.length);
    const finalString = `${'*'.repeat(prevLength)}${trimmed}`;
    return finalString;
  }
  return '';
};

export const capitalizeFirst = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

export const capitalize = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

export const titleCase = (value: string) =>
  value[0].toUpperCase() + value.slice(1).toLowerCase();

export const DECIMALS_TO_SHOW = 2;
export const FOUR_DECIMALS_TO_SHOW = 4;
export const COP_DECIMALS_TO_SHOW = 6;

export const CURRENCY_IDS = {
  1: Currencies.USD,
  2: Currencies.MXN,
  3: Currencies.COP,
  4: ExtendedCurrencies.EUR,
  5: ExtendedCurrencies.PEN,
  6: ExtendedCurrencies.CLP,
  35: ExtendedCurrencies.BRL,
  16: ExtendedCurrencies.JPY,
  13: ExtendedCurrencies.HKD,
  9: ExtendedCurrencies.CHF,
  8: ExtendedCurrencies.CAD,
  12: ExtendedCurrencies.GBP,
  21: ExtendedCurrencies.PLN,
  38: ExtendedCurrencies.CNY,
};

export const getCurrencySymbol = (currencyCode?: string): string => {
  try {
    const parts = new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currencyCode || 'MXN',
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);

    const currencyPart = parts.find((part) => part.type === 'currency');
    return currencyPart ? currencyPart.value : currencyCode || '$';
  } catch {
    return '$';
  }
};

export const parseEmailFromUrlParam = (emailParam: string | null): string => {
  if (!emailParam) return '';

  try {
    let normalizedEmail = emailParam;

    if (normalizedEmail.includes(' ')) {
      normalizedEmail = normalizedEmail.replace(/ /g, '+');
    }

    return decodeURIComponent(normalizedEmail);
  } catch {
    return emailParam;
  }
};

export const displayValue = (value: string | null | undefined): string =>
  value && value.trim() !== '' ? value : '-';

export const displayPercentage = (value: string | null | undefined): string => {
  const cleanValue = displayValue(value);
  return cleanValue === '-' ? '-' : `${cleanValue}%`;
};

export const formatFullName = (
  firstName?: string | null,
  lastName?: string | null,
  companyName?: string | null
): string => {
  const fullName = [firstName, lastName]
    .filter((name) => name && name.trim())
    .map((name) => name?.trim())
    .join(' ');

  return fullName || companyName?.trim() || '';
};
